import { reactive } from 'vue'
import { shopifyFetch } from './client'
import {
  CART_QUERY,
  CART_CREATE_MUTATION,
  CART_LINES_ADD_MUTATION,
  CART_LINES_REMOVE_MUTATION,
} from './queries'

const STORAGE_KEY = 'waf_cart_id'

// Module-level singleton so every component sees the same cart.
const state = reactive({
  id: null,
  checkoutUrl: null,
  totalQuantity: 0,
  lines: [],
  cost: null,
  loading: false,
  error: null,
  drawerOpen: false,
})

function openDrawer() {
  state.drawerOpen = true
}

function closeDrawer() {
  state.drawerOpen = false
}

function applyCart(cart) {
  state.id = cart.id
  state.checkoutUrl = cart.checkoutUrl
  state.totalQuantity = cart.totalQuantity
  state.lines = cart.lines.nodes
  state.cost = cart.cost
  localStorage.setItem(STORAGE_KEY, cart.id)
}

function throwIfUserErrors(userErrors) {
  if (userErrors?.length) {
    throw new Error(userErrors.map((e) => e.message).join('; '))
  }
}

async function loadCart() {
  const existingId = localStorage.getItem(STORAGE_KEY)
  if (!existingId) return

  state.loading = true
  state.error = null
  try {
    const data = await shopifyFetch(CART_QUERY, { id: existingId })
    // Shopify carts expire after ~10 days; a missing cart just means start fresh.
    if (data.cart) applyCart(data.cart)
    else localStorage.removeItem(STORAGE_KEY)
  } catch (err) {
    state.error = err.message
  } finally {
    state.loading = false
  }
}

async function addToCart(variantId, quantity = 1) {
  state.loading = true
  state.error = null
  try {
    if (!state.id) {
      const data = await shopifyFetch(CART_CREATE_MUTATION, {
        lines: [{ merchandiseId: variantId, quantity }],
      })
      throwIfUserErrors(data.cartCreate.userErrors)
      applyCart(data.cartCreate.cart)
    } else {
      const data = await shopifyFetch(CART_LINES_ADD_MUTATION, {
        cartId: state.id,
        lines: [{ merchandiseId: variantId, quantity }],
      })
      throwIfUserErrors(data.cartLinesAdd.userErrors)
      applyCart(data.cartLinesAdd.cart)
    }
  } catch (err) {
    state.error = err.message
    throw err
  } finally {
    state.loading = false
  }
}

async function removeFromCart(lineId) {
  if (!state.id) return
  state.loading = true
  state.error = null
  try {
    const data = await shopifyFetch(CART_LINES_REMOVE_MUTATION, {
      cartId: state.id,
      lineIds: [lineId],
    })
    throwIfUserErrors(data.cartLinesRemove.userErrors)
    applyCart(data.cartLinesRemove.cart)
  } catch (err) {
    state.error = err.message
    throw err
  } finally {
    state.loading = false
  }
}

export function useCart() {
  return { state, loadCart, addToCart, removeFromCart, openDrawer, closeDrawer }
}
